
import { supabase } from '@/integrations/supabase/client';

export interface AuditEvent {
  event_type: string;
  details?: Record<string, any>;
  ip_address?: string;
  user_agent?: string;
}

export const logSecurityEvent = async (event: AuditEvent) => {
  try {
    // Simplified logging without causing circular dependencies
    // Just log to console for now to avoid RLS issues
    console.log('Security event:', event);
    
    // Only attempt database logging for critical events
    if (event.event_type.includes('VIOLATION') || event.event_type.includes('ATTACK')) {
      const { error } = await supabase
        .from('security_audit_logs')
        .insert([{
          user_id: null, // Let the system handle this
          event_type: event.event_type,
          details: event.details || {},
          ip_address: event.ip_address,
          user_agent: event.user_agent || (typeof navigator !== 'undefined' ? navigator.userAgent : null)
        }]);

      if (error) {
        console.error('Failed to log security event:', error);
      }
    }
  } catch (error) {
    console.error('Security audit logging failed:', error);
  }
};

export const validatePhoneNumber = (phone: string): boolean => {
  // Enhanced phone number validation
  const cleanPhone = phone.replace(/\D/g, '');
  
  // Prevent common test/fake numbers
  const suspiciousPatterns = [
    /test/i,
    /fake/i,
    /123456/,
    /000000/,
    /111111/,
    /555555/
  ];
  
  for (const pattern of suspiciousPatterns) {
    if (pattern.test(phone)) {
      return false;
    }
  }
  
  // Australian mobile numbers: 04XXXXXXXX or +614XXXXXXXX
  const australianMobileRegex = /^(\+614|04)\d{8}$/;
  const internationalRegex = /^\+\d{10,15}$/;
  
  return australianMobileRegex.test(phone) || internationalRegex.test(phone);
};

export const sanitizeInput = (input: string): string => {
  return input
    .trim()
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .replace(/['"]/g, '') // Remove quotes to prevent injection
    .substring(0, 1000); // Limit length
};

export const isValidUUID = (uuid: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

// Enhanced input validation
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) && email.length <= 254;
};

export const validateUsername = (username: string): boolean => {
  // Alphanumeric, underscore, hyphen, 3-30 chars
  const usernameRegex = /^[a-zA-Z0-9_-]{3,30}$/;
  return usernameRegex.test(username);
};

// Detect suspicious activity patterns
export const detectSuspiciousActivity = (details: Record<string, any>): boolean => {
  const suspiciousIndicators = [
    // Multiple rapid requests from same identifier
    details.rapid_requests && details.rapid_requests > 10,
    // Unusual user agent patterns
    details.user_agent && /bot|crawler|spider/i.test(details.user_agent),
    // Suspicious phone number patterns
    details.phone && !validatePhoneNumber(details.phone),
    // Multiple failed verifications
    details.failed_verifications && details.failed_verifications > 3
  ];
  
  return suspiciousIndicators.some(indicator => indicator === true);
};
